import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import ProtectedRoute from './auth/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import GeneratorPage from './pages/GeneratorPage';
import ComposerPage from './composer/ComposerPage';
import StudioPage from './studio/StudioPage';
import HistoryPage from './pages/HistoryPage';
import CreditsPage from './pages/CreditsPage';
import FavouritesPage from './pages/catalog/FavouritesPage';
import PublicSongPage from './pages/PublicSongPage';
import { AppShell } from './app-shell/AppShell';
import { AudienceTracker } from './audience/AudienceTracker';
import { GoogleAnalytics } from './audience/GoogleAnalytics';
import { captureFromUrl as captureReferralFromUrl } from './referrals/referralStore';

// Sweep the URL for `?ref=CODE` on very first module load so we catch it
// before React Router / OAuth redirects rewrite the address. Idempotent
// and fire-once — the referralStore strips the param after reading.
captureReferralFromUrl();

// Same trick for `?pending=<id>` from the widget hand-off. Third-party
// widgets top-navigate visitors here; if they're anonymous, ProtectedRoute
// (or the native login submit, or the Google-OAuth round-trip) will
// bounce them through /login before they reach GeneratorPage — and the
// query param can be lost anywhere along the way (Cognito state, WAF
// param stripping, main-service redirect layer). Stashing the id in
// localStorage on the app-origin as soon as we see it means AuthCallbackPage
// and GeneratorPage hydration can rescue it even if the URL loses it.
// Cleared by GeneratorPage after successful hydration; TTL is a soft
// 30-min cap enforced on read.
(function stashPendingFromUrl() {
  try {
    const q = new URLSearchParams(window.location.search);
    const id = q.get('pending');
    if (id && /^[a-f0-9]{24}$/.test(id)) {
      localStorage.setItem('mg.pending.id', id);
      localStorage.setItem('mg.pending.exp', String(Date.now() + 30 * 60 * 1000));
    }
  } catch { /* ignore private-mode / disabled storage */ }
})();

/**
 * app.mixgenie.ai — the generator/library app. This bundle has no
 * catalog / lyric surface (that lives on dicelacancion.com's own
 * bundle) and no CONVERSION_ENABLED gate — auth, generator, credits
 * are always on because that's the whole point of this surface.
 *
 * AppShell is mounted ONCE as a layout route (via <Outlet />), not
 * inside every page component. If each page rendered its own
 * <AppShell>, tab-switching would unmount+remount the GlobalAudioPlayer
 * inside and reset any currently-playing track to 0:00. Hoisting it
 * here keeps the audio element and its state alive across navigation.
 *
 * Login / Register / Forgot / AuthCallback are outside the layout
 * because they render their own full-viewport dark backgrounds and
 * shouldn't show the app chrome.
 */
function AppShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AudienceTracker />
        <GoogleAnalytics />
        <Routes>
          {/* Unshelled auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />

          {/* Public share page — no auth, renders its own chrome. Recipients
              of a share link tap this, hear the song, and see the "Create
              your own" CTA. */}
          <Route path="/s/:jobId" element={<PublicSongPage />} />

          {/* Composer redesign (feat/composer, P2). Standalone route —
              renders its own header + workspace + composer bar, mirroring
              the pictools one-page shape. Anon-visitable; the primary
              action (Generate) soft-bounces to /login if unauth. Legacy
              /generator (below, wrapped in AppShell) stays intact so we
              can compare + roll back with a one-line change. */}
          <Route path="/composer" element={<ComposerPage />} />

          {/* Studio v1 — light-touch DAW mixer over a song's 4 stems
              (vocals/drums/bass/other, split by Demucs via PromptLab).
              Standalone route — renders its own header + workspace so we
              can go full-bleed for the mixer. Anon-visitable to make the
              share link work; the split CTA soft-bounces to /login. */}
          <Route path="/studio/:jobId" element={<StudioPage />} />

          {/* Everything else lives inside a single, persistent AppShell */}
          <Route element={<AppShellLayout />}>
            <Route path="/generator" element={<ProtectedRoute><GeneratorPage /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
            <Route path="/songs" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
            <Route path="/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
            <Route path="/favourites" element={<ProtectedRoute><FavouritesPage /></ProtectedRoute>} />
            {/* Library page retired 2026-08-25 — reference audio lives in the
                Generator's own picker. Old bookmarks and the PWA share_target
                (POST /shared → redirects here with ?shared=1) land on the
                generator, which drains the shared-inbox cache into the
                reference chip. */}
            <Route path="/library" element={<Navigate to="/generator" replace />} />
            <Route path="/shared" element={<Navigate to="/generator?shared=1" replace />} />
          </Route>

          {/* Composer cutover 2026-09-30 — every new signup should land on
              the new composer, not the legacy /generator page. Old bookmarks
              to /generator still resolve (the route is above, wrapped in
              AppShell) so power users on the old UI aren't broken, but the
              default plus every unknown path goes to /composer. */}
          <Route path="/" element={<Navigate to="/composer" replace />} />
          <Route path="*" element={<Navigate to="/composer" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
